import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Building2, Mail, MapPin, Send, AlertCircle, Loader2 } from "lucide-react";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Spec-defined service category list. The backend normalizes these to
// canonical slugs via RESOURCE_CATEGORY_ALIASES — the public form does
// not need to know about slugs at all.
const SERVICE_CATEGORIES = [
  "Mental Health Services", "Substance Use Support", "Housing Support",
  "Food Assistance", "Transportation", "Employment Support",
  "Financial Assistance", "Maternal & Family Wellness", "Veteran Services",
  "Disability Support Services", "Youth Services", "Senior Services",
  "Crisis Support", "Education/Training", "Legal Assistance",
  "Healthcare Services",
];

const REFERRAL_METHODS = [
  { value: "phone",         label: "Phone" },
  { value: "email",         label: "Email" },
  { value: "online_form",   label: "Online Referral Form" },
  { value: "warm_handoff",  label: "Warm Handoff" },
  { value: "other",         label: "Other" },
];

const MOU_BLOCKS = [
  {
    key: "confidentiality_ack",
    title: "Confidentiality",
    body: "All care seeker information received through HarborWell referrals will remain confidential. Information will only be used for coordination of services and support, and will not be shared outside of authorized referral activities.",
  },
  {
    key: "referral_collaboration_ack",
    title: "Referral Collaboration",
    body: "Our organization agrees to (a) receive appropriate referrals from HarborWell, (b) notify HarborWell regarding referral outcomes whenever possible, and (c) refer individuals to HarborWell when additional support, wellness services, or resource navigation may be needed.",
  },
  {
    key: "data_reporting_ack",
    title: "Data & Reporting",
    body: "Referral information and outcomes may be used by HarborWell for grant reporting, community impact reporting, identifying service gaps, and improving care coordination. All reporting will protect individual confidentiality.",
  },
];

const SIGNATURE_ACKS = [
  { key: "authorized_to_sign",          label: "I am authorized to submit information on behalf of this organization." },
  { key: "agree_to_participate",        label: "Our organization agrees to participate as a HarborWell Community Resource Partner." },
  { key: "directory_management_ack",    label: "Our organization understands HarborWell maintains and manages the official Resource Directory." },
];

export default function PublicPartnerApply() {
  const [form, setForm] = useState({
    organization_name: "", website: "", main_phone: "", main_email: "",
    physical_address: "", mailing_address: "",
    counties_served: "", service_area: "",
    hours_of_operation: "", after_hours_availability: "",
    languages_available: "",
    primary_contact_name: "", primary_contact_title: "",
    primary_contact_email: "", primary_contact_phone: "",
    referral_contact_name: "", referral_contact_email: "", referral_contact_phone: "",
    preferred_referral_methods: [],
    preferred_referral_method_other: "",
    selected_categories: [],
    other_category: "",
    description: "", eligibility_requirements: "",
    populations_served: "", age_requirements: "",
    cost_payment_info: "", insurance_accepted: "",
    required_documents: "", referral_requirements: "",
    estimated_wait_time: "",
    mou: {
      confidentiality_ack: false,
      referral_collaboration_ack: false,
      data_reporting_ack: false,
      authorized_to_sign: false,
      agree_to_participate: false,
      directory_management_ack: false,
    },
    signature: { name: "", title: "", signature_text: "" },
    honeypot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMou = (k, v) => setForm((f) => ({ ...f, mou: { ...f.mou, [k]: v } }));
  const setSig = (k, v) => setForm((f) => ({ ...f, signature: { ...f.signature, [k]: v } }));
  const toggleArr = (k, val) => setForm((f) => ({
    ...f,
    [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val],
  }));

  const allMouChecked = Object.values(form.mou).every(Boolean);
  const required = form.organization_name.trim() && form.primary_contact_name.trim()
    && /\S+@\S+\.\S+/.test(form.primary_contact_email)
    && form.signature.name.trim() && form.signature.title.trim() && form.signature.signature_text.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!allMouChecked) {
      toast.error("Please acknowledge every partnership agreement statement.");
      return;
    }
    if (!required) {
      toast.error("Please complete the required fields and signature.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        counties_served: form.counties_served.split(",").map((s) => s.trim()).filter(Boolean),
        languages_available: form.languages_available.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${API_URL}/api/public/partner-submissions`, payload);
      // Defense in depth: the server silently drops honeypot hits by
      // returning {ok:true, id:null}. If we get that shape back, the
      // form was flagged as a bot — most likely because a browser
      // extension re-populated the trap. Do NOT show the thank-you
      // screen; surface a retry so a legitimate applicant is never
      // ghosted after a good-faith submission.
      if (!data?.id) {
        toast.error(
          "Your submission was flagged by our anti-spam filter. Please " +
          "disable browser autofill for this form and try again.",
        );
        return;
      }
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="bg-white max-w-xl w-full rounded-2xl border border-[#E5E5E0] p-8 text-center" data-testid="partner-apply-thanks">
          <div className="flex justify-center mb-4">
            <HarborWellFormHeader
              align="center"
              size="lg"
              linkLogoHome={false}
              testId="partner-apply-thanks-brand-header"
            />
          </div>
          <div className="w-14 h-14 rounded-full bg-[#DCFCE7] text-[#166534] mx-auto flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#0F172A] mb-2">Thank you — your submission was received.</h1>
          <p className="text-sm text-[#334155]">
            HarborWell will review your application and follow up with the primary contact listed on the form.
            You&apos;ll hear back within a few business days.
          </p>
          <Link to="/" className="inline-flex items-center mt-6 text-sm font-medium text-[#1E3A8A] underline">Return to HarborWell Connect</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E5E5E0] px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <HarborWellFormHeader
            eyebrow="HarborWell Connect"
            title="Community Resource Partner Application"
            size="lg"
            testId="partner-apply-brand-header"
            rightSlot={
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#166534] bg-[#DCFCE7] px-2 py-0.5 rounded-full font-semibold">
                <ShieldCheck className="w-3 h-3" /> Confidential submission
              </span>
            }
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <p className="text-sm text-[#334155] mb-6">
          Submit your organization&apos;s information below. HarborWell will review your application,
          electronically signed agreement, and follow up. Information you provide here will not be
          published to the directory until a HarborWell administrator approves your submission.
        </p>

        <form onSubmit={submit} className="space-y-8" data-testid="partner-apply-form">

          {/* Honeypot — hidden from humans, and named so autofill / password
              managers do NOT target it. Any legitimate-looking name (like
              "company_extra") gets populated by autocomplete engines even
              with autoComplete="off", which silently drops real submissions
              via the server's bot filter. The obfuscated name + autoComplete
              off + off-screen positioning + tabindex/aria settings together
              keep the trap effective against bots while making it invisible
              to legitimate applicants and their password managers. */}
          <input
            type="text" name="hp_x9k2q_verify" value={form.honeypot}
            onChange={(e) => set("honeypot", e.target.value)}
            tabIndex="-1" autoComplete="off"
            aria-hidden="true" aria-label="Leave this field blank"
            className="absolute -left-[9999px] w-px h-px opacity-0"
          />

          {/* Section 1 — Organization */}
          <Section icon={<Building2 className="w-4 h-4" />} title="1 · Organization Information">
            <Grid>
              <Field label="Organization Name *" value={form.organization_name} onChange={(v) => set("organization_name", v)} testId="org-name" />
              <Field label="Website" value={form.website} onChange={(v) => set("website", v)} placeholder="https://example.org" testId="website" />
              <Field label="Main Phone Number" value={form.main_phone} onChange={(v) => set("main_phone", v)} testId="main-phone" />
              <Field label="Main Email Address" value={form.main_email} onChange={(v) => set("main_email", v)} type="email" testId="main-email" />
              <Field label="Physical Address" value={form.physical_address} onChange={(v) => set("physical_address", v)} testId="physical-address" className="md:col-span-2" />
              <Field label="Mailing Address (if different)" value={form.mailing_address} onChange={(v) => set("mailing_address", v)} testId="mailing-address" className="md:col-span-2" />
              <Field label="Counties Served (comma-separated)" value={form.counties_served} onChange={(v) => set("counties_served", v)} placeholder="Wayne, Lenoir, …" testId="counties-served" />
              <Field label="Service Area / Region" value={form.service_area} onChange={(v) => set("service_area", v)} testId="service-area" />
              <Field label="Hours of Operation" value={form.hours_of_operation} onChange={(v) => set("hours_of_operation", v)} testId="hours" />
              <Field label="After-Hours / Emergency Availability" value={form.after_hours_availability} onChange={(v) => set("after_hours_availability", v)} testId="after-hours" />
              <Field label="Languages Available (comma-separated)" value={form.languages_available} onChange={(v) => set("languages_available", v)} placeholder="English, Spanish, …" testId="languages" className="md:col-span-2" />
            </Grid>
          </Section>

          {/* Section 2 — Contact */}
          <Section icon={<Mail className="w-4 h-4" />} title="2 · Contact Information">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mt-1 mb-2">Primary Organization Contact</h3>
            <Grid>
              <Field label="Name *" value={form.primary_contact_name} onChange={(v) => set("primary_contact_name", v)} testId="primary-name" />
              <Field label="Title" value={form.primary_contact_title} onChange={(v) => set("primary_contact_title", v)} testId="primary-title" />
              <Field label="Email *" value={form.primary_contact_email} onChange={(v) => set("primary_contact_email", v)} type="email" testId="primary-email" />
              <Field label="Phone" value={form.primary_contact_phone} onChange={(v) => set("primary_contact_phone", v)} testId="primary-phone" />
            </Grid>
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mt-5 mb-2">Referral Contact (if different)</h3>
            <Grid>
              <Field label="Name / Department" value={form.referral_contact_name} onChange={(v) => set("referral_contact_name", v)} testId="referral-name" />
              <Field label="Email" value={form.referral_contact_email} onChange={(v) => set("referral_contact_email", v)} type="email" testId="referral-email" />
              <Field label="Phone" value={form.referral_contact_phone} onChange={(v) => set("referral_contact_phone", v)} testId="referral-phone" />
            </Grid>
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mt-5 mb-2">Preferred referral methods</h3>
            <div className="flex flex-wrap gap-2">
              {REFERRAL_METHODS.map((m) => (
                <Toggle key={m.value} active={form.preferred_referral_methods.includes(m.value)} onClick={() => toggleArr("preferred_referral_methods", m.value)} testId={`ref-method-${m.value}`}>
                  {m.label}
                </Toggle>
              ))}
            </div>
            {form.preferred_referral_methods.includes("other") && (
              <Field label="Other method — describe" value={form.preferred_referral_method_other} onChange={(v) => set("preferred_referral_method_other", v)} testId="referral-other-text" className="mt-3" />
            )}
          </Section>

          {/* Section 3 — Service Profile */}
          <Section icon={<MapPin className="w-4 h-4" />} title="3 · Resource Profile Information">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mb-2">Service Categories (check all that apply)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SERVICE_CATEGORIES.map((cat) => (
                <Toggle key={cat} active={form.selected_categories.includes(cat)} onClick={() => toggleArr("selected_categories", cat)} testId={`cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {cat}
                </Toggle>
              ))}
            </div>
            <Field label="Other category (free text)" value={form.other_category} onChange={(v) => set("other_category", v)} testId="other-category" className="mt-3" />
            <Grid className="mt-4">
              <TextArea label="Description of Services" value={form.description} onChange={(v) => set("description", v)} testId="description" className="md:col-span-2" />
              <TextArea label="Eligibility Requirements" value={form.eligibility_requirements} onChange={(v) => set("eligibility_requirements", v)} testId="eligibility" className="md:col-span-2" />
              <Field label="Populations Served" value={form.populations_served} onChange={(v) => set("populations_served", v)} testId="populations" />
              <Field label="Age Requirements" value={form.age_requirements} onChange={(v) => set("age_requirements", v)} testId="age-req" />
              <Field label="Cost / Payment Information" value={form.cost_payment_info} onChange={(v) => set("cost_payment_info", v)} testId="cost-payment" />
              <Field label="Insurance Accepted" value={form.insurance_accepted} onChange={(v) => set("insurance_accepted", v)} testId="insurance" />
              <Field label="Required Documents" value={form.required_documents} onChange={(v) => set("required_documents", v)} testId="required-docs" />
              <Field label="Referral Requirements" value={form.referral_requirements} onChange={(v) => set("referral_requirements", v)} testId="ref-requirements" />
              <Field label="Estimated Wait Time for Services" value={form.estimated_wait_time} onChange={(v) => set("estimated_wait_time", v)} testId="wait-time" />
            </Grid>
          </Section>

          {/* Section 4 — MOU */}
          <Section icon={<ShieldCheck className="w-4 h-4" />} title="4 · Community Resource Partner Agreement">
            <div className="space-y-4">
              {MOU_BLOCKS.map((b) => (
                <MouCheck key={b.key} block={b} value={form.mou[b.key]} onChange={(v) => setMou(b.key, v)} />
              ))}
              <div className="rounded-xl border border-[#E5E5E0] bg-[#F9F8F6] p-4 text-xs text-[#334155]">
                <strong className="text-[#0F172A]">Referral status updates</strong>
                <p className="mt-1">Partners agree to provide updates such as: referral received · contact attempted · unable to reach care seeker · care seeker connected successfully · appointment scheduled · services provided · unable to assist · referred elsewhere.</p>
                <p className="mt-2">HarborWell will also complete care seeker follow-ups at approximately 30, 60, and/or 90 days to assess outcomes and identify additional needs.</p>
              </div>
            </div>
          </Section>

          {/* Section 5 — E-signature */}
          <Section icon={<Send className="w-4 h-4" />} title="5 · Electronic Signature">
            <div className="space-y-3 mb-4">
              {SIGNATURE_ACKS.map((a) => (
                <label key={a.key} className="flex items-start gap-2 cursor-pointer text-sm text-[#0F172A]">
                  <input
                    type="checkbox"
                    checked={form.mou[a.key]}
                    onChange={(e) => setMou(a.key, e.target.checked)}
                    data-testid={`sig-ack-${a.key.replace(/_/g, "-")}`}
                    className="mt-0.5 w-4 h-4 accent-[#1E3A8A]"
                  />
                  <span>{a.label}</span>
                </label>
              ))}
            </div>
            <Grid>
              <Field label="Organization Representative (full name) *" value={form.signature.name} onChange={(v) => setSig("name", v)} testId="sig-name" />
              <Field label="Title *" value={form.signature.title} onChange={(v) => setSig("title", v)} testId="sig-title" />
              <Field label="Electronic Signature (type your full name) *" value={form.signature.signature_text} onChange={(v) => setSig("signature_text", v)} testId="sig-text" className="md:col-span-2" />
            </Grid>
            <p className="text-[11px] text-[#334155] mt-2">By typing your name, you are signing this agreement electronically. A timestamp + IP address will be recorded with your submission.</p>
          </Section>

          {!allMouChecked && (
            <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-3 text-xs text-[#7F1D1D] flex items-start gap-2" data-testid="mou-warning">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>Please acknowledge every partnership statement above before submitting.</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#E5E5E0]">
            <Link to="/" className="text-sm text-[#334155] hover:underline">Cancel</Link>
            <button
              type="submit"
              disabled={submitting || !allMouChecked || !required}
              data-testid="partner-apply-submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit application
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="bg-white border border-[#E5E5E0] rounded-2xl p-5 md:p-6">
      <h2 className="font-display text-lg font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg bg-[#DBEAFE] text-[#1E3A8A] inline-flex items-center justify-center">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Grid({ children, className = "" }) {
  return <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${className}`}>{children}</div>;
}

function Field({ label, value, onChange, type = "text", placeholder, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-[#334155]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] focus:ring-offset-0"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-[#334155]">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={3}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] resize-y"
      />
    </label>
  );
}

function Toggle({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#0F172A] border-[#E5E5E0] hover:bg-[#F1F5F9]"}`}
    >
      {children}
    </button>
  );
}

function MouCheck({ block, value, onChange }) {
  return (
    <label className="flex items-start gap-3 bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={`mou-${block.key.replace(/_/g, "-")}`}
        className="mt-1 w-4 h-4 accent-[#1E3A8A]"
      />
      <span>
        <span className="block text-sm font-semibold text-[#0F172A]">{block.title}</span>
        <span className="block text-xs text-[#334155] mt-0.5">{block.body}</span>
      </span>
    </label>
  );
}
